import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router";

import { useTrackActivity } from "src/activity/useTrackActivity";
import { type RouterInputs, type RouterOutputs, useTRPC } from "src/api/trpc";
import { usePermissions } from "src/auth/usePermissions";

export type NotificationItem = RouterOutputs["v1"]["notifications"]["list"][number];
type NotificationListInput = NonNullable<RouterInputs["v1"]["notifications"]["list"]>;

/** How often the bell and badges look for news while the tab is open (no push channel yet). */
const POLL_INTERVAL_MS = 60_000;

/** Unread counts for the bell, the Activity badge and each solution's "N new". */
export const useUnreadNotifications = () => {
	const trpc = useTRPC();
	const { canReadNotifications } = usePermissions();
	return useQuery({
		...trpc.v1.notifications.unreadSummary.queryOptions(),
		enabled: canReadNotifications,
		refetchInterval: POLL_INTERVAL_MS
	});
};

export const useNotificationList = (input: NotificationListInput, enabled = true) => {
	const trpc = useTRPC();
	const { canReadNotifications } = usePermissions();
	return useQuery({
		...trpc.v1.notifications.list.queryOptions(input),
		enabled: canReadNotifications && enabled,
		refetchInterval: POLL_INTERVAL_MS
	});
};

/**
 * Every notification, a page at a time (Activity). Each page resumes after the last one's
 * (occurredAt, id), so ties on the timestamp are never skipped.
 */
export const useNotificationPages = (input: { unreadOnly: boolean; limit: number }) => {
	const trpc = useTRPC();
	const { canReadNotifications } = usePermissions();
	return useInfiniteQuery({
		...trpc.v1.notifications.list.infiniteQueryOptions(input, {
			getNextPageParam: (page: NotificationItem[]) => {
				const last = page.at(-1);
				return page.length === input.limit && last ? { occurredAt: last.occurredAt, id: last.id } : undefined;
			}
		}),
		enabled: canReadNotifications,
		refetchInterval: POLL_INTERVAL_MS
	});
};

/** Marking read, and opening a notification: marks it read, then goes where its Review button points. */
export const useNotificationActions = () => {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const track = useTrackActivity();
	const invalidate = useCallback(
		() => queryClient.invalidateQueries({ queryKey: trpc.v1.notifications.pathKey() }),
		[queryClient, trpc]
	);
	const markRead = useMutation(trpc.v1.notifications.markRead.mutationOptions({ onSuccess: invalidate }));
	const markAllRead = useMutation(trpc.v1.notifications.markAllRead.mutationOptions({ onSuccess: invalidate }));

	const open = useCallback(
		async (notification: NotificationItem) => {
			track({ type: "cta.clicked", data: { ctaId: "notification.review", label: notification.type } });
			if (!notification.readAt) markRead.mutate({ ids: [notification.id] });
			await navigate(notification.path);
		},
		[markRead, navigate, track]
	);

	return { open, markRead, markAllRead };
};
